import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { Search, Edit3, LogOut, ChevronDown, ChevronUp, Save, X, Trash2, BookOpen } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Field groups for grouped Admin editor (all fields already exist in MongoDB schema)
const FIELD_GROUPS = [
  { title: 'Información Básica', fields: [
    { key: 'official_name', label: 'Nombre Oficial' },
    { key: 'community_name', label: 'Nombre en la Comunidad' },
    { key: 'library_type', label: 'Tipo de Biblioteca' },
    { key: 'municipality', label: 'Municipio' },
    { key: 'status', label: 'Estatus' },
    { key: 'zone', label: 'Zona' },
  ]},
  { title: 'Contacto', fields: [
    { key: 'email', label: 'Email', type: 'email' },
    { key: 'phone', label: 'Teléfono' },
    { key: 'website', label: 'Sitio Web', type: 'url' },
  ]},
  { title: 'Ubicación', fields: [
    { key: 'physical_address', label: 'Dirección Física', longText: true },
    { key: 'postal_address', label: 'Dirección Postal', longText: true },
    { key: 'location_link', label: 'Enlace a Mapa', type: 'url' },
    { key: 'plus_code', label: 'Plus Code' },
  ]},
  { title: 'Horarios', fields: [
    { key: 'operating_hours', label: 'Horarios de Operación', longText: true,
      hint: 'Formato: "LUNES: 9:00 AM - 5:00 PM; MARTES: ...; SÁBADO: CERRADO". Se sincroniza automáticamente al horario estructurado.' },
  ]},
  { title: 'Servicios', fields: [
    { key: 'services', label: 'Servicios (lista, separada por coma)', longText: true, isList: true },
    { key: 'government_services', label: 'Servicios Gubernamentales', longText: true },
    { key: 'accessibility', label: 'Accesibilidad', longText: true },
    { key: 'other_services', label: 'Otros Servicios', longText: true },
  ]},
  { title: 'Recursos', fields: [
    { key: 'resources', label: 'Recursos (lista, separada por coma)', longText: true, isList: true },
    { key: 'accessibility_resources', label: 'Recursos de Accesibilidad', longText: true },
    { key: 'audiovisual_equipment', label: 'Equipo Audiovisual', longText: true },
    { key: 'lending_equipment', label: 'Equipo Prestable', longText: true },
    { key: 'special_resources', label: 'Recursos Especiales', longText: true },
  ]},
  { title: 'Colecciones', fields: [
    { key: 'collection_size', label: 'Tamaño de la Colección' },
    { key: 'has_catalog', label: '¿Tiene Catálogo?' },
    { key: 'special_collections', label: 'Colecciones Especiales', longText: true },
    { key: 'other_special_collections', label: 'Otras Colecciones Especiales', longText: true },
  ]},
  { title: 'Personal / Organización', fields: [
    { key: 'head_librarian', label: 'Bibliotecario/a a Cargo' },
    { key: 'staff_count', label: 'Cantidad de Personal', type: 'number' },
    { key: 'volunteer_count', label: 'Cantidad de Voluntarios', type: 'number' },
    { key: 'part_of_network', label: '¿Parte de Red?' },
    { key: 'network_name', label: 'Nombre de la Red' },
    { key: 'opening_date', label: 'Fecha de Apertura' },
  ]},
  { title: 'Tour Virtual', fields: [
    { key: 'virtual_tour', label: 'URL del Tour Virtual', type: 'url',
      hint: 'Deja vacío para ocultar el botón "Explorar biblioteca" en la vista pública.' },
  ]},
];

const EditForm = ({ editData, setEditData, onSave, onCancel, saving, libId }) => {
  const upd = (k, v) => setEditData(p => ({ ...p, [k]: v }));
  return (
    <div data-testid={`edit-form-${libId}`}>
      {FIELD_GROUPS.map(group => (
        <fieldset key={group.title} style={{
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '8px',
          padding: '10px 14px',
          marginBottom: '10px',
          background: 'rgba(255,255,255,0.02)'
        }}>
          <legend style={{
            padding: '0 8px',
            fontSize: '12px',
            fontWeight: 600,
            color: '#f39c12',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>{group.title}</legend>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {group.fields.map(f => {
              const val = editData[f.key] ?? '';
              return (
                <div key={f.key} style={{ gridColumn: f.longText ? '1 / -1' : 'auto' }}>
                  <label htmlFor={`fld-${libId}-${f.key}`} style={{
                    fontSize: '11px', color: 'rgba(255,255,255,0.6)',
                    marginBottom: '4px', display: 'block', fontWeight: 500
                  }}>{f.label}</label>
                  {f.longText ? (
                    <textarea
                      id={`fld-${libId}-${f.key}`}
                      data-testid={`admin-field-${f.key}`}
                      value={val}
                      onChange={e => upd(f.key, e.target.value)}
                      rows={3}
                      style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                    />
                  ) : (
                    <input
                      id={`fld-${libId}-${f.key}`}
                      data-testid={`admin-field-${f.key}`}
                      type={f.type || 'text'}
                      value={val}
                      onChange={e => upd(f.key, e.target.value)}
                      style={inputStyle}
                    />
                  )}
                  {f.hint && (
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginTop: '3px' }}>
                      {f.hint}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </fieldset>
      ))}
      <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
        <button data-testid={`save-btn-${libId}`} onClick={onSave} disabled={saving} style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '10px 20px', background: 'linear-gradient(135deg, #2ecc71, #27ae60)',
          border: 'none', borderRadius: '8px', color: '#fff',
          cursor: saving ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600
        }}>
          <Save size={15} /> {saving ? 'Guardando...' : 'Guardar'}
        </button>
        <button onClick={onCancel} data-testid={`cancel-btn-${libId}`} style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '10px 20px', background: 'rgba(255,255,255,0.1)',
          border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px',
          color: '#fff', cursor: 'pointer', fontSize: '13px'
        }}>
          <X size={15} /> Cancelar
        </button>
      </div>
    </div>
  );
};

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  background: 'rgba(0,0,0,0.3)',
  border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: '6px',
  color: '#fff',
  fontSize: '13px',
  outline: 'none',
  boxSizing: 'border-box',
};

const AdminDashboard = ({ onBack }) => {
  const { user, token, logout } = useAuth();
  const [libraries, setLibraries] = useState([]);
  const [search, setSearch] = useState('');
  const [municipality, setMunicipality] = useState('');
  const [municipalities, setMunicipalities] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [stats, setStats] = useState(null);

  const fetchLibraries = useCallback(async () => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (municipality) params.append('municipality', municipality);
    params.append('limit', '500');
    const res = await fetch(`${BACKEND_URL}/api/libraries?${params}`);
    if (res.ok) setLibraries(await res.json());
  }, [search, municipality]);

  useEffect(() => { fetchLibraries(); }, [fetchLibraries]);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/municipalities`)
      .then(r => r.ok ? r.json() : { municipalities: [] })
      .then(d => setMunicipalities(d.municipalities || []));
  }, []);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/dashboard`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.ok ? r.json() : null)
      .then(d => d && setStats(d.statistics));
  }, [token]);

  const startEdit = (lib) => {
    setEditingId(lib.id);
    // Load ALL editable content fields from MongoDB, preserving types.
    // Lists (services/resources) → editable as comma-separated string; converted back on save.
    setEditData({
      // Basic Information
      official_name: lib.official_name || '',
      community_name: lib.community_name || '',
      library_type: lib.library_type || '',
      municipality: lib.municipality || '',
      status: lib.status || '',
      zone: lib.zone || '',
      // Contact
      email: lib.email || '',
      phone: lib.phone || '',
      website: lib.website || '',
      // Location
      physical_address: lib.physical_address || '',
      postal_address: lib.postal_address || '',
      location_link: lib.location_link || '',
      plus_code: lib.plus_code || '',
      // Hours (auto-syncs to detailed_schedule on backend)
      operating_hours: lib.operating_hours || '',
      // Services
      services: Array.isArray(lib.services) ? lib.services.join(', ') : (lib.services || ''),
      government_services: lib.government_services || '',
      accessibility: lib.accessibility || '',
      other_services: lib.other_services || '',
      // Resources
      resources: Array.isArray(lib.resources) ? lib.resources.join(', ') : (lib.resources || ''),
      accessibility_resources: lib.accessibility_resources || '',
      audiovisual_equipment: lib.audiovisual_equipment || '',
      lending_equipment: lib.lending_equipment || '',
      special_resources: lib.special_resources || '',
      // Collections
      collection_size: lib.collection_size || '',
      has_catalog: lib.has_catalog || '',
      special_collections: lib.special_collections || '',
      other_special_collections: lib.other_special_collections || '',
      // Staffing / Organization
      head_librarian: lib.head_librarian || '',
      staff_count: lib.staff_count ?? '',
      volunteer_count: lib.volunteer_count ?? '',
      part_of_network: lib.part_of_network || '',
      network_name: lib.network_name || '',
      opening_date: lib.opening_date || '',
      // Virtual Tour
      virtual_tour: lib.virtual_tour || '',
    });
    setExpandedId(lib.id);
  };

  const LIST_FIELDS = ['services', 'resources'];
  const INT_FIELDS = ['staff_count', 'volunteer_count'];

  // Build a JSON payload from editData, preserving MongoDB field types.
  const buildPayload = (data) => {
    const out = {};
    for (const [k, v] of Object.entries(data)) {
      if (LIST_FIELDS.includes(k)) {
        // Convert comma-separated string → array (skip if empty string)
        const trimmed = (v || '').trim();
        out[k] = trimmed === '' ? [] : trimmed.split(',').map(s => s.trim()).filter(Boolean);
      } else if (INT_FIELDS.includes(k)) {
        const trimmed = (v ?? '').toString().trim();
        // Empty → omit (avoids clobbering with null); numeric → int
        if (trimmed === '') continue;
        const n = parseInt(trimmed, 10);
        if (!Number.isNaN(n)) out[k] = n;
      } else {
        out[k] = v ?? '';
      }
    }
    return out;
  };

  const saveEdit = async (libId) => {
    setSaving(true); setMsg('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/libraries/${libId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(buildPayload(editData))
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Error'); }
      setMsg('Guardado exitosamente');
      setEditingId(null);
      fetchLibraries();
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setMsg(`Error: ${e.message}`);
    } finally { setSaving(false); }
  };

  const deleteLibrary = async (libId, name) => {
    if (!window.confirm(`¿Eliminar "${name}"? Esta acción no se puede deshacer.`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/libraries/${libId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Error'); }
      setMsg('Biblioteca eliminada');
      fetchLibraries();
      setTimeout(() => setMsg(''), 3000);
    } catch (e) { setMsg(`Error: ${e.message}`); }
  };

  const inputStyle = {
    width: '100%', padding: '8px 10px',
    background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '6px', color: '#fff', fontSize: '13px',
    outline: 'none', boxSizing: 'border-box'
  };

  return (
    <div data-testid="admin-dashboard" style={{
      minHeight: 'calc(100vh - 200px)',
      color: '#fff',
      background: '#1e293b',
      borderRadius: '14px',
      padding: '24px',
      boxShadow: '0 4px 18px rgba(0,0,0,0.18)'
    }}>
      {/* Header bar */}
      <div style={{
        padding: '16px 20px', marginBottom: '20px',
        background: 'rgba(0,0,0,0.2)', borderRadius: '12px',
        border: '1px solid rgba(255,255,255,0.08)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <BookOpen size={24} color="#e94560" />
          <div>
            <h1 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Panel de Administrador</h1>
            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>{user?.email}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={onBack} style={{
            padding: '8px 14px', background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px',
            color: '#fff', cursor: 'pointer', fontSize: '13px'
          }}>Volver</button>
          <button data-testid="admin-logout-btn" onClick={logout} style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '8px 14px', background: 'rgba(233,69,96,0.2)',
            border: '1px solid rgba(233,69,96,0.3)', borderRadius: '8px',
            color: '#e94560', cursor: 'pointer', fontSize: '13px'
          }}>
            <LogOut size={16} /> Cerrar Sesión
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {[
            { label: 'Bibliotecas', value: stats.total_libraries, color: '#3498db' },
            { label: 'Usuarios CRM', value: stats.total_users, color: '#2ecc71' },
            { label: 'Activos', value: stats.active_users, color: '#f39c12' }
          ].map(s => (
            <div key={s.label} style={{
              flex: '1 1 140px', padding: '14px 18px',
              background: 'rgba(255,255,255,0.05)', borderRadius: '10px',
              border: '1px solid rgba(255,255,255,0.08)'
            }}>
              <div style={{ fontSize: '26px', fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {msg && (
        <div data-testid="admin-message" style={{
          padding: '10px 16px', marginBottom: '14px', borderRadius: '8px',
          background: msg.startsWith('Error') ? 'rgba(233,69,96,0.15)' : 'rgba(46,204,113,0.15)',
          border: `1px solid ${msg.startsWith('Error') ? 'rgba(233,69,96,0.3)' : 'rgba(46,204,113,0.3)'}`,
          color: msg.startsWith('Error') ? '#e94560' : '#2ecc71', fontSize: '14px'
        }}>{msg}</div>
      )}

      {/* Search and Municipality Filter */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 300px' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.3)' }} />
          <input data-testid="admin-search" type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nombre..."
            style={{
              width: '100%', padding: '12px 12px 12px 40px',
              background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '10px', color: '#fff', fontSize: '14px',
              outline: 'none', boxSizing: 'border-box'
            }}
          />
        </div>
        <select
          data-testid="admin-municipality-filter"
          value={municipality}
          onChange={e => setMunicipality(e.target.value)}
          style={{
            padding: '12px 14px', minWidth: '180px',
            background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '10px', color: '#fff', fontSize: '14px',
            outline: 'none', appearance: 'auto', cursor: 'pointer'
          }}
        >
          <option value="" style={{ background: '#1a1a2e' }}>Todos los municipios</option>
          {municipalities.map(m => (
            <option key={m} value={m} style={{ background: '#1a1a2e' }}>{m}</option>
          ))}
        </select>
      </div>

      <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', marginBottom: '10px' }}>
        {libraries.length} biblioteca(s)
      </div>

      {/* Library list */}
      {libraries.map(lib => (
        <div key={lib.id} style={{
          background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '10px', marginBottom: '6px', overflow: 'hidden'
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 14px', cursor: 'pointer'
          }} onClick={() => setExpandedId(expandedId === lib.id ? null : lib.id)}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lib.official_name}</div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
                {lib.municipality} | {lib.library_type} | {lib.email || 'Sin email'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0, marginLeft: '8px' }}>
              <button data-testid={`edit-btn-${lib.id}`}
                onClick={e => { e.stopPropagation(); startEdit(lib); }}
                style={{
                  padding: '5px 10px', background: 'rgba(52,152,219,0.2)',
                  border: '1px solid rgba(52,152,219,0.3)', borderRadius: '6px',
                  color: '#3498db', cursor: 'pointer', fontSize: '12px',
                  display: 'flex', alignItems: 'center', gap: '4px'
                }}>
                <Edit3 size={13} /> Editar
              </button>
              <button onClick={e => { e.stopPropagation(); deleteLibrary(lib.id, lib.official_name); }}
                style={{
                  padding: '5px 8px', background: 'rgba(233,69,96,0.15)',
                  border: '1px solid rgba(233,69,96,0.3)', borderRadius: '6px',
                  color: '#e94560', cursor: 'pointer', fontSize: '12px'
                }}>
                <Trash2 size={13} />
              </button>
              {expandedId === lib.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </div>

          {expandedId === lib.id && (
            <div style={{ padding: '14px', borderTop: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.15)' }}>
              {editingId === lib.id ? (
                <EditForm
                  editData={editData}
                  setEditData={setEditData}
                  onSave={() => saveEdit(lib.id)}
                  onCancel={() => setEditingId(null)}
                  saving={saving}
                  libId={lib.id}
                />
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '13px' }}>
                  {[
                    ['Nombre', lib.official_name], ['Conocida como', lib.community_name],
                    ['Email', lib.email], ['Teléfono', lib.phone],
                    ['Dirección', lib.physical_address], ['Horario', lib.operating_hours],
                    ['Sitio web', lib.website], ['Colección', lib.collection_size],
                    ['Bibliotecario', lib.head_librarian], ['Apertura', lib.opening_date],
                  ].filter(([, v]) => v).map(([l, v]) => (
                    <div key={l}>
                      <span style={{ color: 'rgba(255,255,255,0.4)' }}>{l}: </span>
                      <span style={{ color: 'rgba(255,255,255,0.8)' }}>{v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default AdminDashboard;
